import { Purchases, LogLevel, CustomerInfo, Offerings, PurchasesError, ErrorCode } from '@revenuecat/purchases-js';
import { REVENUECAT_CONFIG, RevenueCatEntitlement } from '../config/revenuecat.config';

export interface SubscriptionStatus {
  isActive: boolean;
  productIdentifier?: string;
  expirationDate?: Date;
  willRenew?: boolean;
  entitlements: string[];
}

export interface PurchaseResult {
  success: boolean;
  error?: string;
}

export interface UserAttributes {
  email?: string;
  displayName?: string;
  phoneNumber?: string;
  [key: string]: string | undefined;
}

class RevenueCatService {
  private isInitialized = false;

  /**
   * Initialize RevenueCat SDK
   */
  async initialize(userId?: string): Promise<void> {
    try {
      if (this.isInitialized) {
        return;
      }

      // Check if API key is properly configured
      if (!REVENUECAT_CONFIG.publicApiKey || REVENUECAT_CONFIG.publicApiKey === 'your_api_key_here') {
        throw new Error('RevenueCat API Key is not configured. Please set REACT_APP_REVENUECAT_API_KEY in your environment variables.');
      }

      // Configure RevenueCat
      Purchases.setLogLevel(
        REVENUECAT_CONFIG.debugLogsEnabled ? LogLevel.Debug : LogLevel.Info
      );

      // Initialize with public API key and user ID
      await Purchases.configure(REVENUECAT_CONFIG.publicApiKey, userId || '');

      // Test basic functionality after initialization
      try {
        const _customerInfo = await Purchases.getSharedInstance().getCustomerInfo();
        const _offerings = await Purchases.getSharedInstance().getOfferings();
      } catch (testError) {
        console.warn('⚠️ RevenueCat initialized but connection test failed:', testError);
      }

      this.isInitialized = true;
    } catch (error) {
      console.error('❌ Failed to initialize RevenueCat:', error);
      
      // Log detailed error information
      if (error && typeof error === 'object') {
        const errorObj = error as Record<string, unknown>;
        console.error('🔍 Initialization error details:', {
          name: errorObj.name,
          message: errorObj.message,
          stack: errorObj.stack,
          code: errorObj.code,
        });
      }
      
      throw error;
    }
  }

  /**
   * Identify user with RevenueCat
   */
  async identifyUser(userId: string): Promise<void> {
    try {
      // For web SDK, we need to reconfigure with the new user ID
      await Purchases.configure(REVENUECAT_CONFIG.publicApiKey, userId);
    } catch (error) {
      console.error('Failed to identify user:', error);
      throw error;
    }
  }

  /**
   * Register user with email and other attributes
   */
  async registerUser(userId: string, email: string, additionalAttributes?: Partial<UserAttributes>): Promise<void> {
    try {
      // First identify the user
      await this.identifyUser(userId);
      
      // Set user attributes including email
      const attributes: UserAttributes = {
        email,
        "$email": email,
        ...additionalAttributes,
      };
      
      await this.setUserAttributes(attributes);
    } catch (error) {
      console.error('Failed to register user:', error);
      throw error;
    }
  }

  /**
   * Set user attributes (email, display name, etc.)
   */
  async setUserAttributes(attributes: UserAttributes): Promise<void> {
    try {
      // Filter out undefined values
      const cleanAttributes: Record<string, string> = {};
      Object.entries(attributes).forEach(([key, value]) => {
        if (value !== undefined) {
          cleanAttributes[key] = value;
        }
      });

      if (Object.keys(cleanAttributes).length > 0) {
        await Purchases.getSharedInstance().setAttributes(cleanAttributes);
        console.log('✅ User attributes set successfully:', cleanAttributes);
      }
    } catch (error) {
      console.error('Failed to set user attributes:', error);
      throw error;
    }
  }

  /**
   * Set user email specifically
   */
  async setUserEmail(email: string): Promise<void> {
    try {
      await this.setUserAttributes({ email });
    } catch (error) {
      console.error('Failed to set user email:', error);
      throw error;
    }
  }

  /**
   * Get user attributes
   */
  async getUserAttributes(): Promise<Record<string, string>> {
    try {
      const customerInfo: CustomerInfo = await Purchases.getSharedInstance().getCustomerInfo();
      // Note: The web SDK might not return attributes in the same way as mobile SDKs
      // This will return whatever attributes are available in customerInfo
      const subscriberAttributes = (customerInfo as CustomerInfo & { subscriberAttributes?: Record<string, string> }).subscriberAttributes;
      return subscriberAttributes || {};
    } catch (error) {
      console.error('Failed to get user attributes:', error);
      return {};
    }
  }

  /**
   * Log out current user
   */
  async logout(): Promise<void> {
    try {
      // For web SDK, we configure with empty user ID
      await Purchases.configure(REVENUECAT_CONFIG.publicApiKey, '');
    } catch (error) {
      console.error('Failed to logout user:', error);
    }
  }

  /**
   * Get current customer info and subscription status
   */
  async getSubscriptionStatus(): Promise<SubscriptionStatus> {
    try {
      const customerInfo: CustomerInfo = await Purchases.getSharedInstance().getCustomerInfo();
      
      // Debug logging
      console.log('🔍 RevenueCat Customer Info:', customerInfo);
      console.log('🔍 Active Entitlements:', customerInfo.entitlements.active);
      console.log('🔍 All Entitlements:', customerInfo.entitlements.all);
      
      // Check if user has any active entitlements
      const activeEntitlements = Object.keys(customerInfo.entitlements.active);
      const isActive = activeEntitlements.length > 0;
      
      console.log('🔍 Active Entitlement Keys:', activeEntitlements);
      console.log('🔍 Is Active:', isActive);
      
      return {
        isActive,
        entitlements: activeEntitlements,
        productIdentifier: isActive ? activeEntitlements[0] : undefined,
      };
    } catch (error) {
      console.error('Failed to get subscription status:', error);
      return {
        isActive: false,
        entitlements: [],
      };
    }
  }

  /**
   * Force refresh subscription status by calling restore purchases
   * This is useful for web SDK where getCustomerInfo doesn't have force refresh
   */
  async refreshSubscriptionStatus(): Promise<SubscriptionStatus> {
    try {
      console.log('🔄 Force refreshing subscription status...');
      
      // For web SDK, calling restore purchases effectively refreshes the customer info
      await this.restorePurchases();
      
      // Now get the updated subscription status
      return await this.getSubscriptionStatus();
    } catch (error) {
      console.error('Failed to force refresh subscription status:', error);
      return {
        isActive: false,
        entitlements: [],
      };
    }
  }

  /**
   * Check if user has specific entitlement
   */
  async hasEntitlement(entitlement: RevenueCatEntitlement): Promise<boolean> {
    try {
      const customerInfo: CustomerInfo = await Purchases.getSharedInstance().getCustomerInfo();
      const entitlementKey = REVENUECAT_CONFIG.entitlements[entitlement];
      
      console.log('🔍 Checking entitlement:', entitlement);
      console.log('🔍 Looking for entitlement key:', entitlementKey);
      console.log('🔍 Available entitlements:', Object.keys(customerInfo.entitlements.active));
      
      const hasEntitlement = entitlementKey in customerInfo.entitlements.active;
      console.log('🔍 Has entitlement result:', hasEntitlement);
      
      return hasEntitlement;
    } catch (error) {
      console.error('Failed to check entitlement:', error);
      return false;
    }
  }

  /**
   * Purchase a product
   */
  async purchaseProduct(productId: string): Promise<PurchaseResult> {
    try {
             // Get offerings to find the package
       const offerings: Offerings = await Purchases.getSharedInstance().getOfferings();
      
      console.log('offerings', offerings);

      if (!offerings.current && Object.keys(offerings.all).length === 0) {
        console.error('❌ No offerings available');
        return {
          success: false,
          error: 'No offerings available. Please check your RevenueCat dashboard and Paddle configuration.',
        };
      }

      // Find the package in current offering
      let packageToPurchase = null;
      if (offerings.current) {
        packageToPurchase = offerings.current.availablePackages.find(
          pkg => pkg.identifier === productId
        );
      }

      // If not found in current offering, search all offerings
      if (!packageToPurchase) {
        for (const [_offeringId, offering] of Object.entries(offerings.all)) {
          packageToPurchase = offering.availablePackages.find(
            pkg => pkg.identifier === productId
          );
          if (packageToPurchase) {
            break;
          }
        }
      }

      if (!packageToPurchase) {
        const availablePackages = offerings.current?.availablePackages.map(p => p.identifier) || [];
        console.error('❌ Package not found. Available:', availablePackages);
        return {
          success: false,
          error: `Product "${productId}" not found. Available: ${availablePackages.join(', ')}`,
        };
      }

      // Make the purchase with detailed error tracking
      let purchaseResult;
      try {
        purchaseResult = await Purchases.getSharedInstance().purchase({
          rcPackage: packageToPurchase,
        });
      } catch (purchaseError) {
        console.error('❌ Purchase call failed:', purchaseError);
        
        // Specific logging for elements_configuration error
        if (purchaseError && typeof purchaseError === 'object') {
          const errorObj = purchaseError as Record<string, unknown>;
          console.error('🔍 Purchase error breakdown:', {
            name: errorObj.name,
            message: errorObj.message,
            stack: errorObj.stack,
            code: errorObj.code,
            errorCode: errorObj.errorCode,
            underlyingErrorMessage: errorObj.underlyingErrorMessage,
          });
          
          // Check if this is the elements_configuration error
          const errorMessage = String(errorObj.message || '');
          if (errorMessage.includes('elements_configuration')) {
            console.error('🚨 ELEMENTS_CONFIGURATION ERROR DETECTED:');
            console.error('  - This is likely a Paddle configuration issue');
            console.error('  - Check your RevenueCat dashboard Web Billing platform');
            console.error('  - Verify Paddle account is properly connected');
            console.error('  - Ensure products are configured in both Paddle and RevenueCat');
            console.error('  - Check if you\'re using the correct API key (sandbox vs production)');
          }
        }
        
        throw purchaseError;
      }

      const { customerInfo: _customerInfo } = purchaseResult;

      return {
        success: true,
      };
    } catch (error) {
      console.error('❌ Purchase failed:', error);
      
      // Log additional error details for Paddle debugging
      if (error && typeof error === 'object') {
        const errorObj = error as Record<string, unknown>;
        console.error('🔍 Error details:', {
          name: errorObj.name,
          message: errorObj.message,
          stack: errorObj.stack,
          code: errorObj.code,
          errorCode: errorObj.errorCode,
        });
      }

      if (error instanceof PurchasesError && error.errorCode === ErrorCode.UserCancelledError) {
        return {
          success: false,
          error: 'Purchase cancelled by user',
        };
      }

      // Handle Paddle-specific errors
      const errorMessage = error instanceof Error ? error.message : 'Purchase failed';
      if (errorMessage.includes('elements_configuration')) {
        return {
          success: false,
          error: 'Payment system configuration error. Please check your Paddle and RevenueCat setup.',
        };
      }

      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Restore purchases (for web, this refreshes customer info)
   */
  async restorePurchases(): Promise<PurchaseResult> {
    try {
      // For web SDK, restore is essentially refreshing customer info
      const _customerInfo: CustomerInfo = await Purchases.getSharedInstance().getCustomerInfo();
      
      return {
        success: true,
      };
    } catch (error) {
      console.error('Restore purchases failed:', error);
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to restore purchases',
      };
    }
  }

  /**
   * Get available offerings
   */
     async getOfferings(currency?: string): Promise<Offerings> {
     try {
       const options = currency ? { currency } : undefined;
       const offerings: Offerings = await Purchases.getSharedInstance().getOfferings(options);
      return offerings;
    } catch (error) {
      console.error('Failed to get offerings:', error);
      throw error;
    }
  }

  /**
   * Get customer info
   */
  async getCustomerInfo(): Promise<CustomerInfo> {
    try {
      const customerInfo: CustomerInfo = await Purchases.getSharedInstance().getCustomerInfo();
      return customerInfo;
    } catch (error) {
      console.error('Failed to get customer info:', error);
      throw error;
    }
  }

  /**
   * Check if RevenueCat is initialized
   */
  get initialized(): boolean {
    return this.isInitialized;
  }
}

// Export singleton instance
export const revenueCatService = new RevenueCatService();
export default revenueCatService; 